// MatrixCard — reusable chrome wrapper around the Phase-0 `Matrix` (PRODOPSAI-396).
//
// Renders one metric matrix with a header row (title + subtitle on the left,
// threshold legend chips on the right) and the inner `<Matrix />` below.
//
// Every visible string and every callback flows through `spec: IMatrixCardSpec`
// — US2 (PRODOPSAI-395) ships these specs from the metric-definition registry
// at `/admin/metric-definitions`, so admins can change copy without a frontend
// deploy. The component does NOT format legend labels — `label` is rendered
// verbatim.
//
// PRODOPSAI-654: the title's info button is the SHARED `MetricInfoPopover`
// (also used by Metric View), which looks the definition up by `metricKey` via
// `useMetricDefinitions`. So this card is no longer pure-presentation — it
// reads the metric-definition cache through that popover (tests must wrap it in
// a `MetricDefinitionsProvider` or mock `useMetricDefinitions`). The old
// `spec.calculation` tooltip is gone: `calculation` now lives only on the
// Definitions Catalogue and the QC calculation reference.
//
// Used 4× by `QualityControlTab` (US5) and 11+ times across Phase 3.
//
// Visual model: copies the chrome rhythm of `metric-view/TrendCard.tsx` —
// same `Card` / `CardHeader` / `CardContent` primitives, same border-radius,
// same header padding, same body padding. Long titles wrap rather than
// truncating. On viewports below `md`, the legend wraps below the title row.

import type { IJiraSyncHealth, ILegendChip, IMatrixCardSpec } from '@skystone/shared';
import { Card, CardContent, CardHeader, MetricInfoPopover } from '@/components/ui';
import { cn } from '@/lib/utils';
import { JiraSyncHealthDot } from '@/components/shared/JiraSyncHealthDot';
import { Matrix } from './Matrix';

// ---------------------------------------------------------------------------
// Module-scope helpers — defined outside the render body to satisfy
// SonarCloud rule S7721 (no helper components inside the render).
// ---------------------------------------------------------------------------

/**
 * Map a discriminated `tone` to a semantic Tailwind background utility.
 *
 * Mockup palette (`--green-mid`, `--amber-mid`, `--red-mid`) maps to existing
 * semantic tokens per the foundation epic's design-tokens decision — no
 * bespoke hex values introduced here.
 */
const TONE_SWATCH_CLASS: Record<ILegendChip['tone'], string> = {
  success: 'bg-success/15 border-success/40',
  warning: 'bg-warning/15 border-warning/40',
  error: 'bg-error/15 border-error/40',
};

interface LegendChipProps {
  chip: ILegendChip;
}

/**
 * One legend entry — small swatch + label in an inline-flex row.
 *
 * Mirrors the rhythm of `metric-view/shared.tsx:13` (`LegendChip`) but is a
 * different concern (threshold tones, not issue types) so it stays local.
 */
function MatrixLegendChip({ chip }: Readonly<LegendChipProps>) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <span
        aria-hidden="true"
        className={cn('inline-block h-2.5 w-3 rounded-sm border', TONE_SWATCH_CLASS[chip.tone])}
      />
      {chip.label}
    </span>
  );
}

interface LegendProps {
  chips: ILegendChip[];
}

function MatrixLegend({ chips }: Readonly<LegendProps>) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      {chips.map((chip) => (
        <MatrixLegendChip key={`${chip.tone}-${chip.label}`} chip={chip} />
      ))}
    </div>
  );
}

interface MatrixCardHeaderProps {
  title: string;
  subtitle: string;
  legend: ILegendChip[];
  /**
   * Metric key used by the shared `MetricInfoPopover` to look the definition up
   * via `useMetricDefinitions`. When the key resolves to a known metric, an
   * info (ⓘ) button next to the title opens the same popover Metric View uses.
   */
  metricKey: string;
  /**
   * Optional Jira sync-health indicator rendered inline next to the title.
   * Threaded in by `QualityControlTab` for the Status Count matrix only — all
   * other matrices pass nothing and no dot renders.
   */
  syncHealth?: IJiraSyncHealth;
}

/**
 * Header row: title + subtitle on the left, legend chips on the right.
 *
 * On `md` and wider the two groups sit side-by-side, separated by `flex-wrap`
 * so the legend drops below on narrower viewports without overflow. Title
 * uses `break-words` so long titles wrap to a new line rather than being
 * truncated with ellipsis.
 */
function MatrixCardHeaderRow({ title, subtitle, legend, metricKey, syncHealth }: Readonly<MatrixCardHeaderProps>) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center gap-2">
          <h3 className="break-words text-sm font-bold leading-tight text-foreground">{title}</h3>
          <MetricInfoPopover metricKey={metricKey} />
          <JiraSyncHealthDot health={syncHealth} />
        </div>
        <p className="text-[11px] text-muted-foreground">{subtitle}</p>
      </div>
      <MatrixLegend chips={legend} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface MatrixCardProps {
  spec: IMatrixCardSpec;
  /**
   * Optional per-project Jira sync-health summary. Rendered as an inline dot
   * next to the title when supplied; absent → no dot. Phase-1 wiring passes
   * this only for the Status Count matrix (US5 / PRODOPSAI-430).
   */
  syncHealth?: IJiraSyncHealth;
}

export function MatrixCard({ spec, syncHealth }: Readonly<MatrixCardProps>) {
  return (
    <Card data-testid="matrix-card" data-metric-key={spec.metricKey}>
      <CardHeader>
        <MatrixCardHeaderRow
          title={spec.title}
          subtitle={spec.subtitle}
          legend={spec.legend}
          metricKey={spec.metricKey}
          syncHealth={syncHealth}
        />
      </CardHeader>
      <CardContent>
        <Matrix spec={spec.matrix} />
      </CardContent>
    </Card>
  );
}

export default MatrixCard;
